import { useToastContext } from "@ttc3k/trekker";
import {
  EnumOwnerType,
  PeopleManySimpleDocument,
  PersonDetailsQuery,
  usePersonDeleteMutation,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import { useLoggedInAs, usePostHog } from "shared/hooks";
import { useMemo, useState } from "react";
import { useStorefrontView } from "apps/storefront/StorefrontViewContext";

const successMessage = "Person deleted successfully";

export type UseDangerZoneProps = {
  person: PersonDetailsQuery["personDetails"];
  onDeleted: () => void;
};

export const useDangerZone = ({ person, onDeleted }: UseDangerZoneProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_DETAILS_MODAL.DANGER_ZONE",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const loggedInAs = useLoggedInAs();
  const { trackPostHogException } = usePostHog();
  const { storefrontId: contextStorefrontId } = useStorefrontView();

  const showDangerZone = useMemo(() => {
    if (!loggedInAs) return false;

    return (
      loggedInAs.type === EnumOwnerType.Organization ||
      loggedInAs.type === EnumOwnerType.Storefront ||
      !!contextStorefrontId
    );
  }, [loggedInAs, contextStorefrontId]);

  const isRemoveDisabled = !!person?.isOnDirectoryOperator;

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const handleConfirmDelete = () => {
    if (isRemoveDisabled) return;
    setIsConfirmingDelete(true);
  };

  // After a deletion, we need to refetch the people list to remove the person from the list
  const [deletePerson, { loading: isDeleting }] = usePersonDeleteMutation({
    refetchQueries: [PeopleManySimpleDocument],
  });

  /**
   * Deletes the person and shows a success or error toast
   * @returns A promise that resolves to the message from the server
   */
  const handleConfirmResponse = async (confirmed: boolean) => {
    setIsConfirmingDelete(false);
    if (!person || !loggedInAs || !confirmed) return;

    try {
      const { data, errors } = await deletePerson({
        variables: {
          id: person._id,
          entityId: loggedInAs?.entityId,
          type: loggedInAs?.type,
        },
      });

      const message = data?.personDelete ?? "";
      if (message === successMessage) {
        toastFactory.create({
          title: tCore("SUCCESS"),
          description: t("MESSAGES.SUCCESS"),
        });
        onDeleted();
      } else {
        throw new Error(errors?.[0]?.message ?? message);
      }
    } catch (error) {
      trackPostHogException(error, {
        fileName: "useDangerZone.ts",
        method: "handleConfirmResponse",
        message: "Error confirming delete",
      });
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description:
          error instanceof Error ? error.message : t("MESSAGES.ERROR"),
      });
    }
  };

  return {
    isConfirmingDelete,
    isDeleting,
    showDangerZone,
    isRemoveDisabled,
    handleConfirmDelete,
    handleConfirmResponse,
  };
};
